import { runInAction, makeAutoObservable, toJS, autorun, observer } from "mobx";
import { fetchVersionData } from "../queries/info";
import {getVersionInfo} from "../lib/configMgr";
import {fetchDatasetMetadata, fetchCategoryMetadata} from "../queries/metadata";
import {ServerError, ServerOfflineError, CORSError} from "../queries/errors";
import {toast} from "react-toastify";
import {wait} from "../lib/utils";
import * as Sentry from "@sentry/react";

export const LOADING = "Loading...";
export const TAXID_NONE = -1;
export const TAXID_HUMAN = 9606;
export const TAXID_MOUSE = 10090;
export const TAXID_RAT = 10116;

const {
  number: VERSION_FE = "",
  suffix: VERSION_FE_SUFFIX = "",
  backend: VERSION_BE_REQUIRED = "",
  templates: VERSION_TEMPLATES_REQUIRED = "",
  data: VERSION_DATA_REQUIRED = ""
} = getVersionInfo().version;

class AppStatusStore {
  _messageList = [];
  _online = true;
  _templatesVersion = null;
  _versionInfo = null;
  // Set to null if server unreachable
  _ready = false;
  _organismRestriction = TAXID_NONE;
  _fetchingMetadata = false;
  _serverOffline = null;
  _corsError = null;
  _fetchingDatasets = {};
  _fetchingCategories = {};
  _datasets = {};
  _categories = {};
  _settings = new Map();

  constructor() {
    makeAutoObservable(this);
    autorun(() => {
      if (!(this.ready && !this.serverOffline && !this.corsError) && !this.fetchingMetadata) {
        this.fetchMetadata()
          .then(() => {
          })
          .catch(() => {
          });
      }
    }, {delay: 300000});
  }

  get settings() {
    return this._settings;
  }

  get versionRequirements() {
    const reportedTemplatesVersion = this.templatesVersion || LOADING;
    const reportedBackendVersion = this.versionInfo?.version || LOADING;
    const reportedDataVersion = this.versionInfo?.es_version || LOADING;
    return {
      remoteLoaded: reportedTemplatesVersion !== LOADING,
      templates: {required: VERSION_TEMPLATES_REQUIRED, given: reportedTemplatesVersion},
      frontend: {required: `${VERSION_FE}${VERSION_FE_SUFFIX ? "-" + VERSION_FE_SUFFIX : ""}`},
      backend: {required: VERSION_BE_REQUIRED, given: reportedBackendVersion},
      data: {required: VERSION_DATA_REQUIRED, given: reportedDataVersion},
    };
  }

  clear = () => {
    this.ready = false;
    this.versionInfo = null;
    this.serverOffline = null;
    this.corsError = null;
    this.messageList = [];
    this.online = true;
    this.organismRestriction = TAXID_NONE;
    this.datasets = {};
    this.categories = {};
  }

  onError = async error => {
    if (error instanceof CORSError) {
      this.corsError = true;
      this.ready = false;
      console.error("CORS error detected:", error.details);
    } else if (error instanceof ServerOfflineError) {
      this.serverOffline = true;
      this.ready = false;
    } else if (error instanceof ServerError && error.data?.contentType?.includes('text/html')) {
      this.serverOffline = true;
      this.ready = false;
      Sentry.withScope(scope => {
        scope.setTag('error_type', 'html_instead_of_json');
        scope.setContext('api_response', {
          url: error.data?.url,
          content_type: error.data?.contentType,
          preview: error.data?.preview,
          status: error.status,
        });
        Sentry.captureException(error);
      });
    }
    throw error;
  }

  fetchDatasetMetadata = async (auth, dsConfig = null) => {
    if (this.datasets[dsConfig]) {
      return this.datasets[dsConfig];
    }
    if (this.fetchingDatasets[dsConfig]) {
      return this.fetchingDatasets[dsConfig];
    }
    return this.fetchingDatasets[dsConfig] = fetchDatasetMetadata(auth, dsConfig)
      .then((datasets) => {
        //console.debug("Dataset metadata loaded", datasets);

        // Validate that we received a proper object, not HTML or a string
        if (!datasets || typeof datasets !== 'object' || Array.isArray(datasets)) {
          const errorMsg = `Invalid dataset metadata response: expected object, got ${typeof datasets}`;
          const previewContent = typeof datasets === 'string'
            ? datasets.substring(0, 100)
            : JSON.stringify(datasets);

          console.error(errorMsg, previewContent);

          // Report to Sentry with context
          Sentry.withScope(scope => {
            scope.setContext('metadata_error', {
              expected_type: 'object',
              received_type: typeof datasets,
              content_preview: previewContent,
              dsConfig: dsConfig,
              endpoint: 'fetchDatasetMetadata'
            });
            Sentry.captureMessage(errorMsg, 'error');
          });

          throw new Error(errorMsg);
        }

        runInAction(() => {
          this.datasets[dsConfig] = Object.fromEntries(
            Object.entries(datasets).map(([key, value]) => [key, { ...value, id: key }])
          );
          this.serverOffline = false;
          this.corsError = false;
        });
        return this.datasets[dsConfig];
      })
      .catch(error => {
        return this.onError(error);
      })
      .finally(() => {
        runInAction(() => this.fetchingDatasets[dsConfig] = false);
      })
  };

  fetchCategoryMetadata = async (auth, dsConfig = null) => {
    if (this.categories[dsConfig]) {
      return this.categories[dsConfig];
    }
    if (this.fetchingCategories[dsConfig]) {
      return this.fetchingCategories[dsConfig];
    }
    return this.fetchingCategories[dsConfig] = fetchCategoryMetadata(auth, dsConfig)
      .then((data) => {
        //console.debug("Categories metadata loaded", data);

        // Validate that we received a proper object, not HTML or a string
        if (!data || typeof data !== 'object' || Array.isArray(data)) {
          const errorMsg = `Invalid category metadata response: expected object, got ${typeof data}`;
          const previewContent = typeof data === 'string'
            ? data.substring(0, 100)
            : JSON.stringify(data);

          console.error(errorMsg, previewContent);

          // Report to Sentry with context
          Sentry.withScope(scope => {
            scope.setContext('metadata_error', {
              expected_type: 'object',
              received_type: typeof data,
              content_preview: previewContent,
              dsConfig: dsConfig,
              endpoint: 'fetchCategoryMetadata'
            });
            Sentry.captureMessage(errorMsg, 'error');
          });

          throw new Error(errorMsg);
        }

        runInAction(() => {
          this.categories[dsConfig] = Object.fromEntries(
            Object.entries(data).map(([key, value]) => [key, { ...value, id: key, key }])
          );
          this.templatesVersion = data._default.templates._version;
          this.serverOffline = false;
          this.corsError = false;
        });
        return this.categories[dsConfig];
      })
      .catch(this.onError)
      .finally(() => {
        runInAction(() => this.fetchingCategories[dsConfig] = false);
      })
  };

  fetchMetadata = async (verbose = false, auth = null) => {
    if (this.ready && (!verbose || this.versionInfo?.verbose)) {
      return {...this.versionInfo};
    }
    if (this.fetchingMetadata) {
      return this.fetchingMetadata;
    }
    return this.fetchingMetadata = fetchVersionData(verbose, auth).then((data) => {
      //console.debug(`Fetched version info ${JSON.stringify(data)}`);
      runInAction(() => {
        this.fetchingMetadata = false;
        this.serverOffline = false;
        this.corsError = false;
        this.versionInfo = {...data, verbose};
      });
      if (data.ready) {
        runInAction(() => this.ready = true);
        return data;
      }
      else if (data.ready === null) {
        const msg = "Refresh page when server is running";
        console.warn(msg);
        toast.warning(msg);
        return data;
      }
      else {
        const msg = "Server is not ready, retrying in 15s";
        console.warn(msg);
        toast.warning(msg);
        return wait(15000, () => this.fetchMetadata());
      }
    })
      .catch(this.onError)
      .finally(() => {
        runInAction(() => this.fetchingMetadata = false);
      })
  }
  clearMessages = ()=> {
    this.messageList = [];
  }

  get compoundFPTypes() {
    // Get all compound-related similarity types from sim_types_available
    // Filter out non-compound types like 'org' and 'set'
    const simTypes = this.versionInfo.sim_types_available || {};
    const compoundTypes = Object.keys(simTypes).filter(type =>
      !['org', 'set'].includes(type)
    );
    // Fall back to cpd_fp_types if sim_types_available is not available
    return compoundTypes.length > 0 ? compoundTypes : toJS(this.versionInfo.cpd_fp_types || ["sim"]);
  }

  get aiTemplates() {
    return toJS(this.versionInfo.ai_templates || {});
  }

  get aiModels() {
    return toJS(this.versionInfo.ai_models_available || {});
  }

  get aiModelDefault() {
    return toJS(this.versionInfo.ai_model_default || {});
  }

  get aiModelContentFilterFallback() {
    return this.versionInfo?.ai_model_content_filter_fallback ?? null;
  }

  get fetchingMetadata() {
    return this._fetchingMetadata;
  }

  set fetchingMetadata(pending) {
    this._fetchingMetadata = pending;
  }

  get fetchingCategories() {
    return this._fetchingCategories;
  }

  set fetchingCategories(fetching) {
    this._fetchingCategories = fetching;
  }

  get fetchingDatasets() {
    return this._fetchingDatasets;
  }

  set fetchingDatasets(fetching) {
    this._fetchingDatasets = fetching;
  }

  get serverOffline() {
    return this._serverOffline;
  }

  set serverOffline(offline) {
    if (offline) {
      // TODO: ping the server every 5s until it comes back
    }
    this._serverOffline = offline;
  }

  get corsError() {
    return this._corsError;
  }

  set corsError(error) {
    // CORS errors require configuration changes, don't retry automatically
    this._corsError = error;
  }

  set templatesVersion(version) {
    this._templatesVersion = version;
  }

  get templatesVersion() {
    return this._templatesVersion;
  }

  get messageList() {
    return this._messageList;
  }
  set messageList(ml) {
    this._messageList = ml;
  }

  get online() {
    return this._online;
  }
  set online(online) {
    this._online = online;
  }
  get versionInfo() {
    return this._versionInfo || {ready: false, imaps_available: []};
  }

  set versionInfo(info) {
    this._versionInfo = info;
  }

  get datasets() {
    return this._datasets || {};
  }

  set datasets(ds) {
    Object.entries(ds).forEach(([key, value]) => {
      value.id = key;
    });
    this._datasets = ds;
  }

  get categories() {
    return this._categories || {};
  }

  set categories(cats)  {
    this._categories = cats;
  }

  get ready() {
    return this._ready;
  }

  set ready(r) {
    this._ready = r;
  }
  get organismRestriction() {
    return toJS(this._organismRestriction);
  }

  set organismRestriction(taxID) {
    this._organismRestriction = taxID;
  }
}

export default AppStatusStore;
